import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { fmtMoney, fmtPct } from '@/lib/format';
import { SectionHeader, StatusBadge, FilterPills, PremiumTable, PageSkeleton, LoadError } from '@/components/dashboard/MetricCard';
import { dashboardApi } from '@/services/dashboardApi';
import { motion } from 'framer-motion';
import { Segmented } from '@/components/dashboard/kit';
import ComisionesResumen from '@/components/dashboard/ComisionesResumen';
import DashboardMisContactos from '@/pages/dashboard/DashboardMisContactos';

/*
  Comisiones (16/10/2026): tres pestañas.
  - Resumen: totales, comisiones de cada mes y porcentajes por nivel (antes «Rendimiento»).
  - Detalle: cada comisión con su estado.
  - Pipeline: las personas que han llegado con su enlace y en qué etapa están (antes «Mis contactos»).

  Detalle — datos reales del CDM (08/10/2026).
  Pagada · Exigible (ya se puede cobrar) · No exigible (aún falta algo: pago del 100 %, firma de T&C o los días de espera).
*/
const STATUSES = ['Todas', 'Pagada', 'Exigible', 'No exigible'];
const fdate = (d) => (d ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : '—');
// Vocabulario del panel: las líneas «FHCT» del CDM son aportaciones
const TYPE_LABELS = { FHCT: 'Aportación' };
// Motivos del CDM: fechas en formato español y «cliente» en lugar de «socio»
const fmtReason = (t) => String(t || '')
  .replace(/(\d{4})-(\d{2})-(\d{2})/g, '$3/$2/$1')
  .replace(/\bEl socio\b/g, 'El cliente');

// Importe en la divisa en la que se paga (la del cliente). Si no es EUR ni USD, solo el equivalente en euros.
function Amount({ c }) {
  const cur = String(c.currency || 'EUR').toUpperCase();
  if (cur === 'USD' && c.amount_original != null) {
    return (
      <span className="whitespace-nowrap">
        <span className="font-medium text-white">{fmtMoney(c.amount_original, 'usd')}</span>
        <span className="block text-[11px] text-gray-500">≈ {fmtMoney(c.amount, 'eur')}</span>
      </span>
    );
  }
  return <span className="font-medium text-white whitespace-nowrap" title={cur !== 'EUR' ? 'Equivalente en euros' : undefined}>{fmtMoney(c.amount, 'eur')}</span>;
}

function Chip({ label, value, tone }) {
  const color = tone === 'good' ? 'text-emerald-300' : tone === 'warn' ? 'text-amber-300' : 'text-white';
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.03] px-4 py-3">
      <p className="text-[11px] text-gray-400 uppercase tracking-wider font-medium">{label}</p>
      <p className={`font-bold text-lg ${color}`}>{value}</p>
    </div>
  );
}

function ComisionesDetalle() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('Todas');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    setLoading(true);
    setError(null);
    dashboardApi.commissions(filter === 'Todas' ? null : filter)
      .then((r) => { setData(r); setError(null); })
      .catch((e) => { console.error(e); setError(e.message || 'Error'); })
      .finally(() => setLoading(false));
  }, [filter, attempt]);

  const s = data?.summary;
  return (
    <div className="space-y-6" data-testid="comisiones-detalle">
      {data?.rates && (
        <p className="text-xs text-gray-400" data-testid="comisiones-tarifa">
          Tarifa vigente: membresías {[data.rates.n1, data.rates.n2, data.rates.n3].map((v) => fmtPct(v)).join(' · ')}
          {data.rates.fhct ? <> · aportaciones {[data.rates.fhct.n1, data.rates.fhct.n2, data.rates.fhct.n3].map((v) => fmtPct(v)).join(' · ')}</> : null}
          {' '}(N1 · N2 · N3).
        </p>
      )}
      {s && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Chip label="Generadas" value={fmtMoney(s.accrued, 'eur')} />
          <Chip label="Pagadas" value={fmtMoney(s.paid, 'eur')} tone="good" />
          <Chip label="Exigibles" value={fmtMoney(s.payable, 'eur')} tone={s.payable > 0 ? 'warn' : undefined} />
          <Chip label="Aún no exigibles" value={fmtMoney(s.not_yet, 'eur')} />
        </div>
      )}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
        <FilterPills options={STATUSES} active={filter} onChange={setFilter} />
        {data && filter !== 'Todas' && <span className="text-xs text-gray-400">Total filtrado (equivalente en euros): <span className="text-white font-medium">{fmtMoney(data.total, 'eur')}</span></span>}
      </div>
      {loading ? (
        <PageSkeleton cards={0} rows={6} />
      ) : error ? (
        <LoadError onRetry={() => setAttempt((n) => n + 1)} />
      ) : (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
          <PremiumTable
            cols={['Cliente', 'Tipo', 'Nivel', '%', 'Importe', 'Fecha', 'Estado']}
            emptyText={filter === 'Todas'
              ? 'Aún no tienes comisiones. Se generan cuando alguien de tu red se hace miembro o hace una aportación; aquí verás cuándo puedes cobrarlas.'
              : 'No hay comisiones con este estado.'}
            rows={(data?.items || []).map((c) => [
              <div>
                <p>{c.client || '—'}</p>
                {c.status !== 'Pagada' && c.reason && <p className="text-[11px] text-gray-500 font-normal mt-0.5">{fmtReason(c.reason)}</p>}
              </div>,
              TYPE_LABELS[c.type] || c.type,
              c.level_label,
              c.percentage != null ? <span className="text-[#c9a84c] font-medium whitespace-nowrap">{fmtPct(c.percentage)}</span> : '—',
              <Amount c={c} />,
              <span className="whitespace-nowrap">{fdate(c.date)}</span>,
              <StatusBadge status={c.status} />,
            ])}
          />
          <p className="text-[11px] text-gray-500 mt-3">
            Una comisión es exigible {data?.payable_days ?? 15} días después de que el cliente pague el 100 % y firme los T&C. Se paga en la misma divisa en la que pagó el cliente, sin conversión; los totales de arriba se muestran como equivalente en euros. Las ventas anteriores al 20/09/2026 conservan la tarifa anterior (10 % · 5 % · 2,5 %).
          </p>
        </motion.div>
      )}
    </div>
  );
}

const TABS = [
  ['resumen', 'Resumen'],
  ['detalle', 'Detalle'],
  ['pipeline', 'Pipeline'],
];
const SUBTITLE = {
  resumen: 'Lo que has generado, lo que ya puedes cobrar y tus porcentajes',
  detalle: 'Cada comisión, de quién viene y cuándo puedes cobrarla',
  pipeline: 'Las personas que han llegado con tu enlace y en qué punto están',
};

export default function DashboardComisiones() {
  const [params, setParams] = useSearchParams();
  const tab = TABS.some(([k]) => k === params.get('tab')) ? params.get('tab') : 'resumen';
  const setTab = (k) => setParams(k === 'resumen' ? {} : { tab: k }, { replace: true });
  return (
    <div className="space-y-6" data-testid="dashboard-comisiones">
      <SectionHeader title="Comisiones" subtitle={SUBTITLE[tab]} />
      <div className="-mt-4"><Segmented tabs={TABS} value={tab} onChange={setTab} /></div>
      {tab === 'resumen' && <ComisionesResumen />}
      {tab === 'detalle' && <ComisionesDetalle />}
      {tab === 'pipeline' && <DashboardMisContactos embedded />}
    </div>
  );
}
